import { PITCH_CONFIDENCE_MIN } from '@muzly/shared';
import type { NativeF0Frame } from '@muzly/shared';

import { displayMidi } from '../../../components';
import { growSungRange } from '../capture';
// 딥임포트(barrel 아님) — `components` barrel은 `DiagnosisIntroStep.tsx`를 거쳐
// `../server`로 되돌아와 `server ↔ components` 순환 import를 만든다.
import { relativeWindow, yOfWindow } from '../components/pitchWindow';

import type { AnalyGraph } from './analyContract';
import type { SungRange } from '../capture';

/**
 * toAnalyGraph — sustain 마지막 시도 프레임 → 소리 안정성 곡선(`AnalyGraph`, 필드 계약과
 * 서버 제약은 `analyContract.ts`의 `AnalyGraph` 주석 참고).
 *
 * 노래 트레이스(`PitchTraceGraph.tsx:70`)의 창 산식(`relativeWindow`)을 빌려 쓴다 —
 * **지속음 화면 자체가 그리는 그림은 아니다.** 지속음 화면(`RecordTestView.tsx:292-299`의
 * `PitchBarTrack`)은 `deviationRatio(midi, latchedTarget(...))`로 기준음 '라'(옥타브는
 * 첫 발성에서 정한다) 대비
 * 편차를 그려 `relativeWindow`도 `sung`(부른 폭 누적창)도 쓰지 않는다. 여기서 굳이
 * `relativeWindow`를 쓰는 이유는 서버로 나가는 그래프도 절대 음높이를 노출하지 않아야
 * 해서고(plan Decisions "`graph$` y 스케일" 행 — 절대 음높이 비노출 방침), 그 상대화
 * 계산을 새로 만들지 않고 노래 화면이 이미 검증한 것을 재사용한 것뿐이다.
 *
 * 창은 2패스로 하나만 잡는다: 1패스가 `growSungRange`(부른 폭 누적)를 프레임 끝까지 돌려
 * 창 하나를 만들고, 2패스가 그 창 하나로 시작~마지막 확신(유성) 프레임을 매핑한다.
 * 프레임마다 창을 다시 잡으면 같은 음이 부른 시점에 따라 다른 y가 된다.
 *
 * 값(0~100)은 `yOfWindow(window, 100)`이 만드는 화면 y(0=창 상단)를 뒤집은 것과 같다 —
 * `100 - y`. `yOfWindow`가 이미 창 밖 음을 가장자리로 물려 clamp하므로 값은 항상
 * 0~100 정수다.
 */

const isConfident = (frame: NativeF0Frame, midi: number | null): boolean =>
  midi != null && frame.confidence >= PITCH_CONFIDENCE_MIN;

/**
 * `values`가 `MAX_POINTS`를 넘으면 서버가 거절한다(`analyContract.ts` `AnalyGraph` 주석 —
 * 값의 서버 상한 근거). 균등 버킷(같은 개수의 원본 표본씩)으로 평균 내 다운샘플한다.
 */
const MAX_POINTS = 1000;

const bucketAverage = (raw: number[], bucketCount: number): number[] => {
  const out: number[] = [];
  for (let i = 0; i < bucketCount; i += 1) {
    const start = Math.floor((i * raw.length) / bucketCount);
    const end = Math.max(
      start + 1,
      Math.floor(((i + 1) * raw.length) / bucketCount),
    );
    const slice = raw.slice(start, end);
    const mean = slice.reduce((sum, v) => sum + v, 0) / slice.length;
    out.push(Math.min(100, Math.max(0, Math.round(mean))));
  }
  return out;
};

/**
 * 인접 timestamp 차의 중앙값(ms). 짝수 개면 가운데 두 값의 평균.
 */
const medianOf = (nums: number[]): number => {
  const sorted = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
};

export const toAnalyGraph = (
  frames: NativeF0Frame[],
): AnalyGraph | undefined => {
  // 라이브 쪽 `smoothedSeen`(`useVoiceCapture.ts`)과 같은 질문을, 테이크가 끝난 뒤
  // 배열 전체에 대고 한 번에 묻는다 — 엔진이 이 테이크에서 스무딩을 실어 왔나.
  // 거짓이면 `displayMidi`가 raw로 떨어진다(스무딩 off·mock 브리지).
  const smoothingActive = frames.some((f) => f.smoothedF0Hz != null);
  // 첫 유성 프레임 전 무성은 버린다(Q8) — 시작 전 침묵은 안정성 곡선이 아니다.
  const startIndex = frames.findIndex((f) =>
    isConfident(f, displayMidi(f, smoothingActive)),
  );
  if (startIndex === -1) {
    return undefined;
  }
  // 마지막 유성(확신) 프레임 뒤도 자른다 — 지속음 테이크는 무성 5초 뒤 자동 정지하는
  // 경로가 있다(`recordTests.ts:94` SUSTAIN_IDLE_STOP_SEC, `useRecordMachine.ts:418-428`).
  // 그 경로로 끝나면 프레임 배열 끝에 무성 꼬리가 남는데, 잘라내지 않으면 그 구간이
  // hold 값(직전 확신값)으로 평평하게 이어져 `duration`이 부풀고 "그 값을 오래
  // 유지했다"처럼 보인다(제한시간 도달·수동 정지로 끝나면 꼬리가 없을 수 있고, 그때는
  // 이 절단이 아무 것도 자르지 않는다). `duration`은 첫 확신~마지막 확신으로 좁힌다.
  let endIndex = startIndex;
  for (let i = frames.length - 1; i >= startIndex; i -= 1) {
    if (isConfident(frames[i], displayMidi(frames[i], smoothingActive))) {
      endIndex = i;
      break;
    }
  }
  const included = frames.slice(startIndex, endIndex + 1);
  if (included.length < 2) {
    // 유성 프레임이 1개뿐이면 간격을 잴 표본 쌍이 없다 — 서버가 `duration <= 0`을 400으로
    // 거절한다(`analyContract.ts` `AnalyGraph.duration` 주석). 그래프 없이 제출한다.
    return undefined;
  }

  // 1패스 — 부른 폭(sungRange)을 프레임 끝까지 누적해 창 하나를 만든다. `growSungRange`
  // 스스로 무성·저신뢰 프레임을 무시하므로 시작 전 구간을 포함해 전체를 돌려도 결과는 같다.
  let sungRange: SungRange | null = null;
  for (const nativeFrame of frames) {
    sungRange = growSungRange(sungRange, nativeFrame, smoothingActive);
  }

  // 재는 것은 벽시계가 아니라 **담은 시간**이다. 일시중단은 세션을 연 채 프레임만 버리므로
  // (`useVoiceCapture.ts:359` pause → `pcmRef.current.detach()`) 멈춘 구간이 인접 timestamp의
  // 큰 간격으로 남는다 — `withinAnalysisWindow`(`useVoiceCapture.ts`)가 같은 이유로 벽시계
  // 대신 "담은 시간"을 쓴다. 간격이 얼마든 곡선은 프레임 순서대로 균등하게 찍히므로,
  // 정상 간격들의 **중앙값**으로 표본 간격을 잡으면 그 큰 간격(일시중단)이 이상치로
  // 밀려나 duration에 섞이지 않는다.
  const gaps: number[] = [];
  for (let i = 1; i < included.length; i += 1) {
    gaps.push(included[i].timestampMs - included[i - 1].timestampMs);
  }
  const rawInterval = Math.max(1, Math.round(medianOf(gaps)));
  const duration = rawInterval * (included.length - 1);

  // 2패스 — 창 하나(위에서 끝까지 누적한 sungRange)로 시작~마지막 확신(유성) 프레임을 매핑한다.
  // 창 산식은 `PitchTraceGraph.tsx:70`(노래 트레이스)에서 빌린 것 — 지속음 화면
  // (`PitchBarTrack`)은 이 창을 그리지 않는다(파일 헤더 주석).
  const window = relativeWindow(sungRange);
  const toY = yOfWindow(window, 100);
  let lastValue = 0;
  const raw = included.map((nativeFrame) => {
    const midi = displayMidi(nativeFrame, smoothingActive);
    if (isConfident(nativeFrame, midi) && midi != null) {
      lastValue = Math.round(100 - toY(midi));
    }
    return lastValue;
  });

  const length = Math.min(raw.length, MAX_POINTS);
  // 다운샘플이 없으면(length === raw.length) 위에서 이미 잰 표본 간격·duration을 그대로
  // 쓴다. 다운샘플이 있으면 버킷 수(length) 기준으로 표본 간격을 다시 잡는다 — duration
  // (담은 시간의 총량)은 그대로 두고 그 시간을 더 적은 버킷에 나눠 담을 뿐이다. `length`는
  // 항상 2 이상(raw.length ≥ 2, `MAX_POINTS` = 1000 ≥ 2)이라 분모가 0이 될 수 없다.
  const values = length === raw.length ? raw : bucketAverage(raw, length);
  const interval =
    length === raw.length
      ? rawInterval
      : Math.max(1, Math.round(duration / (length - 1)));

  return {
    values,
    interval,
    duration: length === raw.length ? duration : interval * (length - 1),
  };
};
